/**
 * GlobalBannerCenter — THE one global top strip (owner directive 2026-08-14).
 *
 * Before this, three independent strips (GlobalLowBalanceBanner,
 * TrialStatusBanner, UsageLimitBanner) mounted adjacently in App.tsx and
 * STACKED when several fired at once. The directive: ONE nicely-placed band
 * in the platform's cream/gold/ink palette; when more than one notice is
 * eligible it ROTATES between them; when none is, no banner renders at all.
 *
 * Architecture: a tiny registration bus. The center wraps the three source
 * components as children so their eligibility/suppression hooks keep running
 * exactly as before — but instead of rendering their own strip, each calls
 * useRegisterBanner(descriptor) and returns null. The center renders the
 * single band:
 *   - 0 registered  → null (no band, no reserved space).
 *   - 1 registered  → the band, no dots.
 *   - 2+ registered → rotate every 8s (paused while hovered) with one dot
 *     per banner (active dot brass-filled; clicking a dot jumps). Critical
 *     tones sort first and rotation restarts on the most severe whenever the
 *     set of banners changes.
 *
 * The X dismisses only the CURRENT banner — it calls that banner's
 * onDismiss; the source then unregisters per its own dismissal policy
 * (session, daily-5AM, per-severity …) and rotation continues with the rest.
 *
 * Placement contract: same as the strips it replaces — full-width band in
 * normal flow directly below the Header; it pushes page content down and
 * never overlays the header (owner report 2026-07-31).
 *
 * Sources rendered OUTSIDE the provider (standalone/tests) see no context
 * and fall back to rendering their own legacy strip.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { BANNER_BAND_PINNED_Z_INDEX } from '../../styles/layers';
import { useChatPanelFullScreen } from '../wallet/chatPanelStore';

export type BannerTone = 'info' | 'warn' | 'critical';

export interface BannerDescriptor {
  /** Stable identity — re-registering the same id replaces in place. */
  id: string;
  tone: BannerTone;
  content: React.ReactNode;
  /** Whether the center shows the X for this banner. */
  dismissible: boolean;
  /** Called when the X is pressed while this banner is current. */
  onDismiss?: () => void;
}

export interface BannerBus {
  register: (banner: BannerDescriptor) => void;
  unregister: (id: string) => void;
}

/**
 * Null outside a GlobalBannerCenter — source components use that to fall
 * back to rendering their own legacy strip when mounted standalone.
 */
export const BannerBusContext = createContext<BannerBus | null>(null);

/** Rotation cadence between banners when several are registered. */
export const BANNER_ROTATE_INTERVAL_MS = 8000;

const TONE_RANK: Record<BannerTone, number> = { critical: 0, warn: 1, info: 2 };

/* Platform palette — the wallet's cream/gold/ink language (NOT the old blue). */
const TONE_STYLES: Record<BannerTone, { bg: string; fg: string; border: string }> = {
  info: { bg: '#FBF7EC', fg: '#6B5320', border: 'rgba(176,140,60,0.28)' },
  warn: { bg: '#FDF3D7', fg: '#7A5714', border: 'rgba(176,140,60,0.45)' },
  critical: { bg: '#FDECE8', fg: '#8C2F1F', border: 'rgba(159,59,45,0.35)' },
};

/**
 * Layer for the PINNED band (fullscreen-chat only) — defined with the rest of
 * the ladder in src/styles/layers.ts and re-exported here so existing import
 * sites keep working.
 */
export { BANNER_BAND_PINNED_Z_INDEX } from '../../styles/layers';

/**
 * CSS custom property carrying the band's measured height. Published on
 * <html> while the band is PINNED so a full-viewport overlay can inset
 * itself below the band instead of covering it. Absent/0 in normal flow,
 * where the band already occupies its own space and pushes content down.
 */
export const BANNER_BAND_HEIGHT_VAR = '--prest0n-banner-band-height';

const Strip = styled.div<{ $tone: BannerTone; $pinned: boolean }>`
  position: ${(p) => (p.$pinned ? 'fixed' : 'relative')};
  ${(p) =>
    p.$pinned
      ? `top: 0; left: 0; right: 0; z-index: ${BANNER_BAND_PINNED_Z_INDEX};`
      : ''}
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap; /* single row on desktop; wraps gracefully on mobile */
  gap: 12px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => TONE_STYLES[p.$tone].fg};
  background: ${(p) => TONE_STYLES[p.$tone].bg};
  border-bottom: 1px solid ${(p) => TONE_STYLES[p.$tone].border};
`;

const Content = styled.span`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  min-width: 0;
`;

const Dots = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

/* Active dot is brass-filled; inactive dots keep a brass outline only. */
const Dot = styled.button<{ $active: boolean }>`
  flex: none;
  width: 8px;
  height: 8px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid #b08c3c;
  background: ${(p) => (p.$active ? '#b08c3c' : 'transparent')};
  transition: background 0.15s ease;
`;

const DismissButton = styled.button`
  flex: none;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  padding: 2px 4px;
  line-height: 0;
  &:hover {
    opacity: 1;
  }
  svg {
    width: 16px;
    height: 16px;
  }
`;

/**
 * Register `banner` with the surrounding GlobalBannerCenter while non-null;
 * replaces by id on change, unregisters on null and on unmount. A no-op when
 * no center is above (the caller should then render its legacy strip —
 * check via useContext(BannerBusContext)).
 */
export function useRegisterBanner(banner: BannerDescriptor | null): void {
  const bus = useContext(BannerBusContext);
  const registeredIdRef = useRef<string | null>(null);

  // Sync after EVERY source render so content stays fresh (balances tick,
  // days count down). This cannot loop: the center's resulting re-render
  // passes identical child elements + a memo-stable context value, so React
  // bails out of re-rendering the sources.
  useEffect(() => {
    if (!bus) return;
    if (banner) {
      if (registeredIdRef.current !== null && registeredIdRef.current !== banner.id) {
        bus.unregister(registeredIdRef.current);
      }
      registeredIdRef.current = banner.id;
      bus.register(banner);
    } else if (registeredIdRef.current !== null) {
      bus.unregister(registeredIdRef.current);
      registeredIdRef.current = null;
    }
  });

  // Unregister on unmount (or if the bus itself is swapped out).
  useEffect(() => {
    return () => {
      if (bus && registeredIdRef.current !== null) {
        bus.unregister(registeredIdRef.current);
        registeredIdRef.current = null;
      }
    };
  }, [bus]);
}

export const GlobalBannerCenter: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<BannerDescriptor[]>([]);

  const register = useCallback((banner: BannerDescriptor) => {
    setBanners((prev) => {
      const i = prev.findIndex((b) => b.id === banner.id);
      if (i === -1) return [...prev, banner];
      const next = prev.slice();
      next[i] = banner; // replace in place — registration order is stable
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setBanners((prev) => (prev.some((b) => b.id === id) ? prev.filter((b) => b.id !== id) : prev));
  }, []);

  // Memo-stable so a center re-render never invalidates the sources' context.
  const bus = useMemo<BannerBus>(() => ({ register, unregister }), [register, unregister]);

  // Severity-first ordering (critical → warn → info), stable within a tone.
  const sorted = useMemo(
    () => [...banners].sort((a, b) => TONE_RANK[a.tone] - TONE_RANK[b.tone]),
    [banners],
  );
  const count = sorted.length;

  const [index, setIndex] = useState(0);
  const [hovered, setHovered] = useState(false);

  // Whenever the SET of banners (or a tone escalation) changes, restart the
  // rotation on the most severe banner (position 0 of the sorted order).
  const compositionKey = sorted.map((b) => `${b.id}:${b.tone}`).join('|');
  useEffect(() => {
    setIndex(0);
  }, [compositionKey]);

  // Rotate every 8s while more than one banner is registered; pause on hover.
  useEffect(() => {
    if (count < 2 || hovered) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, BANNER_ROTATE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [count, hovered]);

  const safeIndex = count > 0 ? index % count : 0;
  const current = count > 0 ? sorted[safeIndex] : null;

  // PINNED mode (2026-08-31 consistency directive): the fullscreen chat
  // takeover is a full-viewport position:fixed layer, so an in-flow band is
  // simply invisible behind it. While that takeover is up the band pins
  // itself to the top of the viewport above it — the same one band, the same
  // sources, the same rotation, just a different place to stand. Everywhere
  // else it stays in normal flow, which /desktop depends on: that page sizes
  // its live-desktop frame from this band's DOCUMENT offset, and a band with
  // zero flow height would both grow the frame and cover its top.
  const chatFullScreen = useChatPanelFullScreen();
  const pinned = chatFullScreen && current !== null;

  // Publish the pinned band's height so the takeover can inset below it.
  // Measured rather than assumed: the copy wraps to two or three lines on
  // narrow viewports, and a hardcoded height would clip it exactly there.
  const stripRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = typeof document !== 'undefined' ? document.documentElement : null;
    if (!root) return;
    if (!pinned) {
      root.style.removeProperty(BANNER_BAND_HEIGHT_VAR);
      return;
    }
    const publish = () => {
      const h = stripRef.current?.getBoundingClientRect().height ?? 0;
      root.style.setProperty(BANNER_BAND_HEIGHT_VAR, `${Math.round(h)}px`);
    };
    publish();
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(publish);
      if (stripRef.current) ro.observe(stripRef.current);
    } catch {
      /* no ResizeObserver — the resize listener below still covers reflows */
    }
    window.addEventListener('resize', publish, { passive: true });
    return () => {
      window.removeEventListener('resize', publish);
      ro?.disconnect();
      root.style.removeProperty(BANNER_BAND_HEIGHT_VAR);
    };
    // Re-measure when the visible banner changes: tones and copy differ in height.
  }, [pinned, current?.id, current?.tone]);

  return (
    <BannerBusContext.Provider value={bus}>
      {current && (
        <Strip
          ref={stripRef}
          $tone={current.tone}
          $pinned={pinned}
          role="status"
          aria-live="polite"
          data-testid="global-banner-center"
          data-banner-pinned={pinned ? 'true' : undefined}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <Content>{current.content}</Content>
          {count > 1 && (
            <Dots>
              {sorted.map((b, i) => (
                <Dot
                  key={b.id}
                  type="button"
                  $active={i === safeIndex}
                  aria-label={`show notice ${i + 1} of ${count}`}
                  onClick={() => setIndex(i)}
                />
              ))}
            </Dots>
          )}
          {current.dismissible && (
            <DismissButton
              type="button"
              aria-label="Dismiss"
              onClick={() => current.onDismiss?.()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </DismissButton>
          )}
        </Strip>
      )}
      {children}
    </BannerBusContext.Provider>
  );
};

export default GlobalBannerCenter;
